import { useState, useRef } from 'react';
import { fileApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Upload, X, Loader2, Image as ImageIcon, FileText } from 'lucide-react';
import { toast } from 'sonner';

export function ImageUpload({ 
  entityType, 
  entityId, 
  currentPath, 
  onUpload, 
  className = "",
  shape = "square", // "square" or "banner"
  label = "Upload Imagem",
  acceptPdf = false, // when true, also accepts PDF files (used for weight tables, certificates, etc.)
  field // optional entity field to store the file path in (e.g. "weight_table_path")
}) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const isPdfPath = (p) => typeof p === 'string' && p.toLowerCase().endsWith('.pdf');
  const currentIsPdf = isPdfPath(currentPath);
  const imageUrl = previewUrl || (currentPath && !currentIsPdf ? fileApi.getUrl(currentPath) : null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validTypes = acceptPdf ? [...validImageTypes, 'application/pdf'] : validImageTypes;
    if (!validTypes.includes(file.type)) {
      toast.error(acceptPdf
        ? 'Tipo invalido. Use JPG, PNG, GIF, WebP ou PDF.'
        : 'Tipo de arquivo inválido. Use JPG, PNG, GIF ou WebP.');
      return;
    }

    // Validate file size — PDFs allowed up to 10MB; images up to 5MB
    const isPdf = file.type === 'application/pdf';
    const maxSize = isPdf ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Arquivo muito grande. Máximo ${maxSize / (1024 * 1024)}MB.`);
      return;
    }

    // Show preview (only for images)
    if (!isPdf) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }

    // Upload if entityId exists
    if (entityId) {
      setUploading(true);
      try {
        const { data } = await fileApi.upload(file, entityType, entityId, field);
        toast.success('Imagem enviada com sucesso!');
        if (onUpload) onUpload(data.path);
      } catch (error) {
        toast.error('Erro ao enviar imagem');
        setPreviewUrl(null);
      } finally {
        setUploading(false);
      }
    } else {
      // Store file for later upload (when creating new entity)
      if (onUpload) onUpload(file);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    if (onUpload) onUpload(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const aspectClass = shape === "banner" 
    ? "aspect-[3/1] w-full max-w-md" 
    : "aspect-square w-32";

  return (
    <div className={`${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptPdf ? "image/jpeg,image/png,image/gif,image/webp,application/pdf" : "image/jpeg,image/png,image/gif,image/webp"}
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {imageUrl ? (
        <div className={`relative ${aspectClass} rounded-lg overflow-hidden bg-[#1A1A1A] border border-[#27272A]`}>
          <img 
            src={imageUrl} 
            alt="Preview" 
            className="w-full h-full object-cover"
          />
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
            </div>
          )}
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
          >
            <X size={16} />
          </button>
        </div>
      ) : currentIsPdf ? (
        <div className={`relative ${aspectClass} rounded-lg overflow-hidden bg-[#1A1A1A] border border-[#27272A] flex flex-col items-center justify-center gap-2 text-[#D4AF37]`}>
          <FileText size={32} />
          <span className="text-xs font-semibold">PDF enviado</span>
          <a href={fileApi.getUrl(currentPath)} target="_blank" rel="noopener noreferrer" className="text-xs underline">Abrir</a>
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={`${aspectClass} rounded-lg border-2 border-dashed border-[#27272A] hover:border-[#D4AF37]/50 flex flex-col items-center justify-center gap-2 text-[#71717A] hover:text-[#A1A1AA] transition-colors bg-[#1A1A1A]`}
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
          ) : (
            <>
              {acceptPdf ? <FileText size={24} /> : <ImageIcon size={24} />}
              <span className="text-xs text-center px-2">{label}</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}
